import {
  type DocumentPanelData,
  type File,
  type ImagePanelConfig,
  type TopicData,
  type PanelState,
  type State,
} from "./schema";
import {
  type CoordinateUnit,
  Datasource,
  Layout,
  LayoutItem,
  LogPanelConfig,
  PlotPanelConfig,
  PlotSeriesConfig,
  ThreeDEntity,
  ThreeDPanelConfig,
} from "./schema/v1";

// Base
export enum ActionType {
  AddClipToImagePanel = "AddClipToImagePanel",
  AddDataToLogPanel = "addDataToLogPanel",
  AddDataToRawMessagePanel = "AddDataToRawMessagePanel",
  AddPathToMapPanel = "AddPathToMapPanel",
  AddSeriesToPlotPanel = "AddSeriesToPlotPanel",
  CreateDocumentPanelWithFile = "CreateDocumentPanelWithFile",
  CreatePanel = "CreatePanel",
  MovePanel = "MovePanel",
  PutImagePanelData = "PutImagePanelData",
  PutFiles = "PutFiles",
  RemoveAllPanels = "RemoveAllPanels",
  RemoveClipFromImagePanel = "RemoveClipFromImagePanel",
  RemoveMessagePathFromLogPanel = "RemoveMessagePathFromLogPanel",
  RemoveMessagePathFromRawMessagePanel = "RemoveMessagePathFromRawMessagePanel",
  RemovePanel = "RemovePanel",
  RemovePathFromMapPanel = "RemovePathFromMapPanel",
  RemoveSeriesFromPlotPanel = "RemoveSeriesFromPlotPanel",
  ReplaceState = "ReplaceState",
  ReplacePanelsAndLayout = "ReplacePanelsAndLayout",
  ResizeLayouts = "ResizeLayouts",
  SetAllLayoutsResizing = "SetAllLayoutsResizing",
  SetDataSource = "SetDataSource",
  SetDatasources = "SetDatasources",
  SetEventVisibility = "SetEventVisibility",
  SetEventsVisibility = "SetEventsVisibility",
  SetImagePanelConfig = "SetImagePanelConfig",
  SetLayoutResizing = "SetLayoutResizing",
  SetLogPanelConfig = "SetLogPanelConfig",
  SetPanelTitle = "SetPanelTitle",
  SetPathCoordinateUnits = "SetPathCoordinateUnits",
  SetPathStyle = "SetPathStyle",
  SetPathVisibility = "SetPathVisibility",
  SetPlotPanelConfig = "SetPlotPanelConfig",
  SetSeriesConfig = "SetSeriesConfig",
  SetSeriesVisibility = "SetSeriesVisibility",
  SetDocumentPanelFile = "SetDocumentPanelFile",
  SetThreeDPanelConfig = "SetThreeDPanelConfig",
  AddThreeDEntity = "AddThreeDEntity",
  UpdateThreeDEntity = "UpdateThreeDEntity",
  RemoveThreeDEntity = "RemoveThreeDEntity",
}

export enum LayoutOrientation {
  TOP,
  RIGHT,
  BOTTOM,
  LEFT,
}

export interface Action<T> {
  payload: T;
  type: ActionType;
}

// AddClipToImagePanel
interface AddClipToImagePanelPayload {
  panelId: string;
  data: TopicData;
}
interface AddClipToImagePanelAction
  extends Action<AddClipToImagePanelPayload> {}
export function isAddClipToImagePanelAction(
  action: Action<unknown>,
): action is AddClipToImagePanelAction {
  return action.type === ActionType.AddClipToImagePanel;
}
export function addClipToImagePanel(
  panelId: string,
  data: TopicData,
): AddClipToImagePanelAction {
  return {
    payload: {
      panelId,
      data,
    },
    type: ActionType.AddClipToImagePanel,
  };
}

// AddDataToLogPanel
interface AddDataToLogPanelPayload {
  data: TopicData;
  panelId: string;
}
interface AddDataToLogPanelAction extends Action<AddDataToLogPanelPayload> {}
export function isAddDataToLogPanelAction(
  action: Action<unknown>,
): action is AddDataToLogPanelAction {
  return action.type === ActionType.AddDataToLogPanel;
}
export function addDataToLogPanel(
  panelId: string,
  data: TopicData,
): AddDataToLogPanelAction {
  return {
    payload: {
      data,
      panelId,
    },
    type: ActionType.AddDataToLogPanel,
  };
}

// AddDataToRawMessagePanel
interface AddDataToRawMessagePanelPayload {
  data: TopicData;
  panelId: string;
}
interface AddDataToRawMessagePanelAction
  extends Action<AddDataToRawMessagePanelPayload> {}
export function isAddDataToRawMessagePanelAction(
  action: Action<unknown>,
): action is AddDataToRawMessagePanelAction {
  return action.type === ActionType.AddDataToRawMessagePanel;
}
export function addDataToRawMessagePanel(
  panelId: string,
  data: TopicData,
): AddDataToRawMessagePanelAction {
  return {
    payload: {
      panelId,
      data,
    },
    type: ActionType.AddDataToRawMessagePanel,
  };
}

// AddPathToMapPanel
interface AddPathToMapPanelPayload {
  coordinateUnits?: {
    lat?: CoordinateUnit;
    lon?: CoordinateUnit;
  };
  data: TopicData[];
  panelId: string;
}
interface AddPathToMapPanelAction extends Action<AddPathToMapPanelPayload> {}
export function isAddPathToMapPanelAction(
  action: Action<unknown>,
): action is AddPathToMapPanelAction {
  return action.type === ActionType.AddPathToMapPanel;
}
export function addPathToMapPanel(
  panelId: string,
  data: TopicData[],
  coordinateUnits?: { lat?: CoordinateUnit; lon?: CoordinateUnit },
): AddPathToMapPanelAction {
  return {
    payload: {
      coordinateUnits,
      data,
      panelId,
    },
    type: ActionType.AddPathToMapPanel,
  };
}

// AddSeriesToPlotPanel
interface AddSeriesToPlotPanelPayload {
  data: TopicData;
  panelId: string;
}
interface AddSeriesToPlotPanelAction
  extends Action<AddSeriesToPlotPanelPayload> {}
export function isAddSeriesToPlotPanelAction(
  action: Action<unknown>,
): action is AddSeriesToPlotPanelAction {
  return action.type === ActionType.AddSeriesToPlotPanel;
}
export function addSeriesToPlotPanel(
  panelId: string,
  data: TopicData,
): AddSeriesToPlotPanelAction {
  return {
    payload: {
      data,
      panelId,
    },
    type: ActionType.AddSeriesToPlotPanel,
  };
}

// CreatePanel
export interface Placement {
  siblingLayout: Layout | LayoutItem;
  orientation: LayoutOrientation;
}
export interface CreatePanelPayload {
  data: TopicData[] | null;
  type: PanelState["type"];
  placement?: Placement;
}
interface CreatePanelAction extends Action<CreatePanelPayload> {}
export function isCreatePanelAction(
  action: Action<unknown>,
): action is CreatePanelAction {
  return action.type === ActionType.CreatePanel;
}
export function createPanelAction(
  data: TopicData[] | null,
  type: PanelState["type"],
  placement?: Placement,
): CreatePanelAction {
  return {
    payload: {
      data,
      type,
      placement,
    },
    type: ActionType.CreatePanel,
  };
}

// CreateDocumentPanelWithFile
interface CreateDocumentPanelWithFilePayload {
  data: DocumentPanelData;
  placement?: Placement;
}
interface CreateDocumentPanelWithFileAction
  extends Action<CreateDocumentPanelWithFilePayload> {}
export function isCreateDocumentPanelWithFileAction(
  action: Action<unknown>,
): action is CreateDocumentPanelWithFileAction {
  return action.type === ActionType.CreateDocumentPanelWithFile;
}
export function createDocumentPanelWithFile(
  data: DocumentPanelData,
  placement?: Placement,
): CreateDocumentPanelWithFileAction {
  return {
    payload: {
      data,
      placement,
    },
    type: ActionType.CreateDocumentPanelWithFile,
  };
}

// MovePanel
export interface MovePanelPayload {
  panelLayout: LayoutItem;
  placement: Placement;
}
interface MovePanelAction extends Action<MovePanelPayload> {}
export function isMovePanelAction(
  action: Action<unknown>,
): action is MovePanelAction {
  return action.type === ActionType.MovePanel;
}
export function movePanelAction(
  panelLayout: LayoutItem,
  placement: Placement,
): MovePanelAction {
  return {
    payload: {
      panelLayout,
      placement,
    },
    type: ActionType.MovePanel,
  };
}

// PutImagePanelData
interface PutImagePanelDataPayload {
  panelId: string;
  data: TopicData;
}
interface PutImagePanelDataAction extends Action<PutImagePanelDataPayload> {}
export function isPutImagePanelDataAction(
  action: Action<unknown>,
): action is PutImagePanelDataAction {
  return action.type === ActionType.PutImagePanelData;
}
export function putImagePanelData(
  panelId: string,
  data: TopicData,
): PutImagePanelDataAction {
  return {
    payload: {
      panelId,
      data,
    },
    type: ActionType.PutImagePanelData,
  };
}

// PutFiles
/** @deprecated Use setDatasources instead */
interface PutFilesPayload {
  files: File[];
}
interface PutFilesAction extends Action<PutFilesPayload> {}
export function isPutFilesAction(
  action: Action<unknown>,
): action is PutFilesAction {
  return action.type === ActionType.PutFiles;
}
/** @deprecated Use setDatasources instead */
export function putFiles(files: File[]): PutFilesAction {
  return {
    payload: { files },
    type: ActionType.PutFiles,
  };
}

// SetDatasources
interface SetDatasourcesPayload {
  datasources: Datasource[];
}
interface SetDatasourcesAction extends Action<SetDatasourcesPayload> {}
export function isSetDatasourcesAction(
  action: Action<unknown>,
): action is SetDatasourcesAction {
  return action.type === ActionType.SetDatasources;
}
export function setDatasources(
  datasources: Datasource[],
): SetDatasourcesAction {
  return {
    payload: { datasources },
    type: ActionType.SetDatasources,
  };
}

// RemoveAllPanels
interface RemoveAllPanelsAction extends Action<undefined> {}
export function isRemoveAllPanelsAction(
  action: Action<unknown>,
): action is RemoveAllPanelsAction {
  return action.type === ActionType.RemoveAllPanels;
}
export function removeAllPanels(): RemoveAllPanelsAction {
  return {
    payload: undefined,
    type: ActionType.RemoveAllPanels,
  };
}

// RemoveClipFromImagePAnel
interface RemoveClipFromImagePanelPayload {
  panelId: string;
  clipId: string;
}
interface RemoveClipFromImagePanelAction
  extends Action<RemoveClipFromImagePanelPayload> {}
export function isRemoveClipFromImagePanelAction(
  action: Action<unknown>,
): action is RemoveClipFromImagePanelAction {
  return action.type === ActionType.RemoveClipFromImagePanel;
}
export function removeClipFromImagePanel(
  panelId: string,
  clipId: string,
): RemoveClipFromImagePanelAction {
  return {
    payload: {
      panelId,
      clipId,
    },
    type: ActionType.RemoveClipFromImagePanel,
  };
}

// RemoveDataFromLogPanel
interface RemoveMessagePathFromLogPanelPayload {
  panelId: string;
  messagePathId: string;
}
interface RemoveMessagePathFromLogPanelAction
  extends Action<RemoveMessagePathFromLogPanelPayload> {}
export function isRemoveMessagePathFromLogPanelAction(
  action: Action<unknown>,
): action is RemoveMessagePathFromLogPanelAction {
  return action.type === ActionType.RemoveMessagePathFromLogPanel;
}
export function removeMessagePathFromLogPanel(
  panelId: string,
  messagePathId: string,
): RemoveMessagePathFromLogPanelAction {
  return {
    payload: {
      panelId,
      messagePathId,
    },
    type: ActionType.RemoveMessagePathFromLogPanel,
  };
}

// RemoveMessagePathFromRawMessagePanel
interface RemoveMessagePathFromRawMessagePanelPayload {
  panelId: string;
  messagePathId: string;
}
interface RemoveMessagePathFromRawMessagePanelAction
  extends Action<RemoveMessagePathFromRawMessagePanelPayload> {}
export function isRemoveMessagePathFromRawMessagePanelAction(
  action: Action<unknown>,
): action is RemoveMessagePathFromRawMessagePanelAction {
  return action.type === ActionType.RemoveMessagePathFromRawMessagePanel;
}
export function removeMessagePathFromRawMessagePanel(
  panelId: string,
  messagePathId: string,
): RemoveMessagePathFromRawMessagePanelAction {
  return {
    payload: {
      panelId,
      messagePathId,
    },
    type: ActionType.RemoveMessagePathFromRawMessagePanel,
  };
}

// RemovePanel
export interface RemovePanelPayload {
  panelId: string;
}
interface RemovePanelAction extends Action<RemovePanelPayload> {}
export function isRemovePanelAction(
  action: Action<unknown>,
): action is RemovePanelAction {
  return action.type === ActionType.RemovePanel;
}
export function removePanel(panelId: string): RemovePanelAction {
  return {
    payload: {
      panelId,
    },
    type: ActionType.RemovePanel,
  };
}

// RemovePathFromMapPanel
interface RemovePathFromMapPanelPayload {
  panelId: string;
  pathId: string;
}
interface RemovePathFromMapPanelAction
  extends Action<RemovePathFromMapPanelPayload> {}
export function isRemovePathFromMapPanelAction(
  action: Action<unknown>,
): action is RemovePathFromMapPanelAction {
  return action.type === ActionType.RemovePathFromMapPanel;
}
export function removePathFromMapPanel(
  panelId: string,
  pathId: string,
): RemovePathFromMapPanelAction {
  return {
    payload: {
      panelId,
      pathId,
    },
    type: ActionType.RemovePathFromMapPanel,
  };
}

// RemoveSeriesFromPlotPanel
interface RemoveSeriesFromPlotPanelPayload {
  panelId: string;
  seriesId: string;
}
interface RemoveSeriesFromPlotPanelAction
  extends Action<RemoveSeriesFromPlotPanelPayload> {}
export function isRemoveSeriesFromPlotPanelAction(
  action: Action<unknown>,
): action is RemoveSeriesFromPlotPanelAction {
  return action.type === ActionType.RemoveSeriesFromPlotPanel;
}
export function removeSeriesFromPlotPanel(
  panelId: string,
  seriesId: string,
): RemoveSeriesFromPlotPanelAction {
  return {
    payload: {
      panelId,
      seriesId,
    },
    type: ActionType.RemoveSeriesFromPlotPanel,
  };
}

// ReplaceState
// Intended only for use by the StateEditor component -- use more specific actions in other contexts.
interface ReplaceStatePayload {
  state: State;
}
interface ReplaceStateAction extends Action<ReplaceStatePayload> {}
export function isReplaceStateAction(
  action: Action<unknown>,
): action is ReplaceStateAction {
  return action.type === ActionType.ReplaceState;
}
export function replaceState(state: State): ReplaceStateAction {
  return {
    payload: {
      state,
    },
    type: ActionType.ReplaceState,
  };
}

// ReplacePanelsAndLayout
interface ReplacePanelsAndLayoutPayload {
  panels: State["panels"];
  layout: State["layout"];
}
interface ReplacePanelsAndLayoutAction
  extends Action<ReplacePanelsAndLayoutPayload> {}
export function isReplacePanelsAndLayoutAction(
  action: Action<unknown>,
): action is ReplacePanelsAndLayoutAction {
  return action.type === ActionType.ReplacePanelsAndLayout;
}
export function replacePanelsAndLayout(
  panels: State["panels"],
  layout: State["layout"],
): ReplacePanelsAndLayoutAction {
  return {
    payload: {
      panels,
      layout,
    },
    type: ActionType.ReplacePanelsAndLayout,
  };
}

// SetPathStyle
interface SetPathStylePayload {
  panelId: string;
  pathId: string;
  style: Record<string, unknown>;
}
interface SetPathStyleAction extends Action<SetPathStylePayload> {}
export function isSetPathStyleAction(
  action: Action<unknown>,
): action is SetPathStyleAction {
  return action.type === ActionType.SetPathStyle;
}
export function setPathStyle(
  panelId: string,
  pathId: string,
  style: Record<string, unknown>,
): SetPathStyleAction {
  return {
    payload: {
      panelId,
      pathId,
      style,
    },
    type: ActionType.SetPathStyle,
  };
}

// SetPathVisibility
interface SetPathVisibilityPayload {
  panelId: string;
  pathId: string;
  visible: boolean;
}
interface SetPathVisibilityAction extends Action<SetPathVisibilityPayload> {}
export function isSetPathVisibilityAction(
  action: Action<unknown>,
): action is SetPathVisibilityAction {
  return action.type === ActionType.SetPathVisibility;
}
export function setPathVisibility(
  panelId: string,
  pathId: string,
  visible: boolean,
): SetPathVisibilityAction {
  return {
    payload: {
      panelId,
      pathId,
      visible,
    },
    type: ActionType.SetPathVisibility,
  };
}

// SetPathCoordinateUnits
interface SetPathCoordinateUnitsPayload {
  panelId: string;
  pathId: string;
  coordinateUnits: {
    lat?: CoordinateUnit;
    lon?: CoordinateUnit;
  };
}
interface SetPathCoordinateUnitsAction
  extends Action<SetPathCoordinateUnitsPayload> {}
export function isSetPathCoordinateUnitsAction(
  action: Action<unknown>,
): action is SetPathCoordinateUnitsAction {
  return action.type === ActionType.SetPathCoordinateUnits;
}
export function setPathCoordinateUnits(
  panelId: string,
  pathId: string,
  coordinateUnits: {
    lat?: CoordinateUnit;
    lon?: CoordinateUnit;
  },
): SetPathCoordinateUnitsAction {
  return {
    payload: {
      panelId,
      pathId,
      coordinateUnits,
    },
    type: ActionType.SetPathCoordinateUnits,
  };
}

// SetSeriesConfig
interface SetSeriesConfigPayload {
  panelId: string;
  seriesId: string;
  config: Partial<PlotSeriesConfig>;
}
interface SetSeriesConfigAction extends Action<SetSeriesConfigPayload> {}
export function isSetSeriesConfigAction(
  action: Action<unknown>,
): action is SetSeriesConfigAction {
  return action.type === ActionType.SetSeriesConfig;
}
export function setSeriesConfig(
  panelId: string,
  seriesId: string,
  config: Partial<PlotSeriesConfig>,
): SetSeriesConfigAction {
  return {
    payload: {
      panelId,
      seriesId,
      config,
    },
    type: ActionType.SetSeriesConfig,
  };
}

// SetSeriesVisibility
interface SetSeriesVisibilityPayload {
  panelId: string;
  seriesId: string;
  visible: boolean;
}
interface SetSeriesVisibilityAction
  extends Action<SetSeriesVisibilityPayload> {}
export function isSetSeriesVisibilityAction(
  action: Action<unknown>,
): action is SetSeriesVisibilityAction {
  return action.type === ActionType.SetSeriesVisibility;
}
export function setSeriesVisibility(
  panelId: string,
  seriesId: string,
  visible: boolean,
): SetSeriesVisibilityAction {
  return {
    payload: {
      panelId,
      seriesId,
      visible,
    },
    type: ActionType.SetSeriesVisibility,
  };
}

// Update dimensions
interface LayoutDimension {
  id: string;
  relativeSize: number;
}
interface ResizeLayoutPayload {
  layout1: LayoutDimension;
  layout2: LayoutDimension;
}

interface ResizeLayoutsAction extends Action<ResizeLayoutPayload> {}
export function isResizeLayoutsAction(
  action: Action<unknown>,
): action is ResizeLayoutsAction {
  return action.type === ActionType.ResizeLayouts;
}
export function resizeLayouts(
  layout1: LayoutDimension,
  layout2: LayoutDimension,
): ResizeLayoutsAction {
  return {
    payload: {
      layout1,
      layout2,
    },
    type: ActionType.ResizeLayouts,
  };
}

// Set all layouts resizing
interface SetAllLayoutsResizingPayload {
  isResizing: boolean;
}
interface SetAllLayoutsResizingAction
  extends Action<SetAllLayoutsResizingPayload> {}
export function isSetAllLayoutsResizingAction(
  action: Action<unknown>,
): action is SetAllLayoutsResizingAction {
  return action.type === ActionType.SetAllLayoutsResizing;
}
export function setAllLayoutsResizing(
  isResizing: boolean,
): SetAllLayoutsResizingAction {
  return {
    payload: {
      isResizing,
    },
    type: ActionType.SetAllLayoutsResizing,
  };
}

interface SetEventVisibilityPayload {
  eventId: string;
  isVisible: boolean;
}
interface SetEventVisibilityAction extends Action<SetEventVisibilityPayload> {}
export function isSetEventVisibilityAction(
  action: Action<unknown>,
): action is SetEventVisibilityAction {
  return action.type === ActionType.SetEventVisibility;
}
export function setEventVisibility(
  eventId: string,
  isVisible: boolean,
): SetEventVisibilityAction {
  return {
    payload: {
      eventId,
      isVisible,
    },
    type: ActionType.SetEventVisibility,
  };
}

interface SetEventsVisibilityPayload {
  isVisible: boolean;
  eventIds: string[];
}
interface SetEventsVisibilityAction
  extends Action<SetEventsVisibilityPayload> {}
export function isSetVisibilityForAllEventsAction(
  action: Action<unknown>,
): action is SetEventsVisibilityAction {
  return action.type === ActionType.SetEventsVisibility;
}
export function setEventsVisibility(
  isVisible: boolean,
  eventIds: string[],
): SetEventsVisibilityAction {
  return {
    payload: {
      isVisible,
      eventIds,
    },
    type: ActionType.SetEventsVisibility,
  };
}

interface SetImagePanelConfigPayload {
  panelId: string;
  config: Partial<ImagePanelConfig>;
}
interface SetImagePanelConfigAction
  extends Action<SetImagePanelConfigPayload> {}
export function isSetImagePanelConfigAction(
  action: Action<unknown>,
): action is SetImagePanelConfigAction {
  return action.type === ActionType.SetImagePanelConfig;
}
export function setImagePanelConfig(
  panelId: string,
  config: Partial<ImagePanelConfig>,
): SetImagePanelConfigAction {
  return {
    payload: {
      panelId,
      config,
    },
    type: ActionType.SetImagePanelConfig,
  };
}

interface SetLogPanelConfigPayload {
  panelId: string;
  config: Partial<LogPanelConfig>;
}
interface SetLogPanelConfigAction extends Action<SetLogPanelConfigPayload> {}
export function isSetLogPanelConfigAction(
  action: Action<unknown>,
): action is SetLogPanelConfigAction {
  return action.type === ActionType.SetLogPanelConfig;
}
export function setLogPanelConfig(
  panelId: string,
  config: Partial<LogPanelConfig>,
): SetLogPanelConfigAction {
  return {
    payload: {
      panelId,
      config,
    },
    type: ActionType.SetLogPanelConfig,
  };
}

interface SetLayoutResizingPayload {
  layoutIds: string[];
  isResizing: boolean;
}
interface SetLayoutResizingAction extends Action<SetLayoutResizingPayload> {}
export function isSetLayoutResizingAction(
  action: Action<unknown>,
): action is SetLayoutResizingAction {
  return action.type === ActionType.SetLayoutResizing;
}
export function setLayoutResizing(
  layoutIds: string[],
  isResizing: boolean,
): SetLayoutResizingAction {
  return {
    payload: {
      layoutIds,
      isResizing,
    },
    type: ActionType.SetLayoutResizing,
  };
}

// SetPanelTitle
interface SetPanelTitlePayload {
  panelId: string;
  title: string;
}
interface SetPanelTitleAction extends Action<SetPanelTitlePayload> {}
export function isSetPanelTitleAction(
  action: Action<unknown>,
): action is SetPanelTitleAction {
  return action.type === ActionType.SetPanelTitle;
}
export function setPanelTitle(
  panelId: string,
  title: string,
): SetPanelTitleAction {
  return {
    payload: {
      panelId,
      title,
    },
    type: ActionType.SetPanelTitle,
  };
}

interface SetDataSourcePayload {
  topicData: TopicData | TopicData[];
  panelId: string;
  /** seriesId for a plot panel, clip id for an image panel, path id for a map panel, etc */
  segmentId?: string;
}
interface SetDataSourceAction extends Action<SetDataSourcePayload> {}
export function isSetDataSourceAction(
  action: Action<unknown>,
): action is SetDataSourceAction {
  return action.type === ActionType.SetDataSource;
}
export function setDataSource(
  payload: SetDataSourcePayload,
): SetDataSourceAction {
  return {
    payload,
    type: ActionType.SetDataSource,
  };
}

interface SetPlotPanelConfigPayload {
  panelId: string;
  config: Partial<PlotPanelConfig>;
}
interface SetPlotPanelConfigAction extends Action<SetPlotPanelConfigPayload> {}
export function isSetPlotPanelConfigAction(
  action: Action<unknown>,
): action is SetPlotPanelConfigAction {
  return action.type === ActionType.SetPlotPanelConfig;
}
export function setPlotPanelConfig(
  panelId: string,
  config: Partial<PlotPanelConfig>,
): SetPlotPanelConfigAction {
  return {
    payload: {
      panelId,
      config,
    },
    type: ActionType.SetPlotPanelConfig,
  };
}

// SetThreeDPanelConfig
interface SetThreeDPanelConfigPayload {
  panelId: string;
  config: Partial<ThreeDPanelConfig>;
}
interface SetThreeDPanelConfigAction
  extends Action<SetThreeDPanelConfigPayload> {}
export function isSetThreeDPanelConfigAction(
  action: Action<unknown>,
): action is SetThreeDPanelConfigAction {
  return action.type === ActionType.SetThreeDPanelConfig;
}
export function setThreeDPanelConfig(
  panelId: string,
  config: Partial<ThreeDPanelConfig>,
): SetThreeDPanelConfigAction {
  return {
    payload: {
      panelId,
      config,
    },
    type: ActionType.SetThreeDPanelConfig,
  };
}

// AddThreeDEntity
interface AddThreeDEntityPayload {
  panelId: string;
  entity: ThreeDEntity;
}
interface AddThreeDEntityAction extends Action<AddThreeDEntityPayload> {}
export function isAddThreeDEntityAction(
  action: Action<unknown>,
): action is AddThreeDEntityAction {
  return action.type === ActionType.AddThreeDEntity;
}
export function addThreeDEntity(
  panelId: string,
  entity: ThreeDEntity,
): AddThreeDEntityAction {
  return {
    payload: {
      panelId,
      entity,
    },
    type: ActionType.AddThreeDEntity,
  };
}

// UpdateThreeDEntity
interface UpdateThreeDEntityPayload {
  panelId: string;
  entityId: string;
  updates: Partial<ThreeDEntity>;
}
interface UpdateThreeDEntityAction extends Action<UpdateThreeDEntityPayload> {}
export function isUpdateThreeDEntityAction(
  action: Action<unknown>,
): action is UpdateThreeDEntityAction {
  return action.type === ActionType.UpdateThreeDEntity;
}
export function updateThreeDEntity(
  panelId: string,
  entityId: string,
  updates: Partial<ThreeDEntity>,
): UpdateThreeDEntityAction {
  return {
    payload: {
      panelId,
      entityId,
      updates,
    },
    type: ActionType.UpdateThreeDEntity,
  };
}

// RemoveThreeDEntity
interface RemoveThreeDEntityPayload {
  panelId: string;
  entityId: string;
}
interface RemoveThreeDEntityAction extends Action<RemoveThreeDEntityPayload> {}
export function isRemoveThreeDEntityAction(
  action: Action<unknown>,
): action is RemoveThreeDEntityAction {
  return action.type === ActionType.RemoveThreeDEntity;
}
export function removeThreeDEntity(
  panelId: string,
  entityId: string,
): RemoveThreeDEntityAction {
  return {
    payload: {
      panelId,
      entityId,
    },
    type: ActionType.RemoveThreeDEntity,
  };
}

// SetDocumentPanelFile
interface SetDocumentPanelFilePayload {
  panelId: string;
  data: DocumentPanelData;
}
interface SetDocumentPanelFileAction
  extends Action<SetDocumentPanelFilePayload> {}
export function isSetDocumentPanelFileAction(
  action: Action<unknown>,
): action is SetDocumentPanelFileAction {
  return action.type === ActionType.SetDocumentPanelFile;
}
export function setDocumentPanelFile(
  panelId: string,
  data: DocumentPanelData,
): SetDocumentPanelFileAction {
  return {
    payload: {
      panelId,
      data,
    },
    type: ActionType.SetDocumentPanelFile,
  };
}
